import React, { useState } from 'react';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Row from 'react-bootstrap/Row';
import Spinner from 'react-bootstrap/Spinner';
import Login from './Login';

const EMPTY_CONTACT = { name: '', email: '', institution: '', message: '' };

// Marketing-style landing page for unauthenticated visitors on the central
// open student tenant (training.medi-case.co.uk). Tells them what MediCase is,
// what the free vs paid tiers include, and gives them a clear path into
// either Sign Up (creates a free account) or Sign In (existing account).
//
// University tenants don't render this — their users know what they're
// signing into and don't have a paywall.
const FEATURES = [
  {
    icon: 'bi-journal-medical',
    title: 'Curated case library',
    body: 'Branching scenarios with rationale, not just multiple choice. Built and reviewed by clinicians.',
  },
  {
    icon: 'bi-clipboard2-pulse',
    title: 'EPMA-style chart',
    body: 'Real prescribing simulator. Allergies and dose checks fire as you type. Witness sign-off for CDs.',
  },
  {
    icon: 'bi-calculator',
    title: 'Calculation exercises',
    body: 'Auto-generated, fixed-answer and multiple-choice questions. Challenge mode tracks personal bests.',
  },
  {
    icon: 'bi-journal-bookmark',
    title: 'Portfolio + CPD',
    body: 'Reflections, outcome tagging and a lecturer-feedback thread that follows each piece of work.',
  },
];

const StudentMarketingLanding = ({
  tenantDisplayName,
  tagline,
  onSubmit,
  authLoading,
  authError,
  onForgotPassword,
  authMode,
  onModeChange,
}) => {
  const [showAuth, setShowAuth] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [contactForm, setContactForm] = useState(EMPTY_CONTACT);
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactStatus, setContactStatus] = useState(null);
  const productName = tenantDisplayName || 'MediCase';

  const openSignUp = () => { onModeChange('register'); setShowAuth(true); };
  const openSignIn = () => { onModeChange('login'); setShowAuth(true); };

  const openContact = () => {
    setContactStatus(null);
    setContactForm(EMPTY_CONTACT);
    setShowContact(true);
  };
  const closeContact = () => {
    if (contactSubmitting) return;
    setShowContact(false);
  };

  const handleContactChange = (field) => (event) => {
    setContactForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleContactSubmit = async (event) => {
    event.preventDefault();
    if (!contactForm.name.trim() || !contactForm.email.trim() || !contactForm.institution.trim()) {
      setContactStatus('validation');
      return;
    }
    setContactSubmitting(true);
    setContactStatus(null);
    try {
      const payload = new FormData();
      payload.append('name', contactForm.name.trim());
      payload.append('email', contactForm.email.trim());
      payload.append('institution', contactForm.institution.trim());
      payload.append('message', contactForm.message.trim());
      payload.append('_subject', `MediCase institution enquiry: ${contactForm.institution.trim()}`);
      payload.append('_template', 'table');
      const response = await fetch('https://formsubmit.co/ajax/ben.logan@auxtechna.co.uk', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: payload,
      });
      const result = await response.json().catch(() => ({}));
      if (response.ok && (result.success === true || result.success === 'true')) {
        setContactStatus('success');
        setContactForm(EMPTY_CONTACT);
      } else {
        setContactStatus('error');
      }
    } catch (_error) {
      setContactStatus('error');
    } finally {
      setContactSubmitting(false);
    }
  };

  return (
    <div className="medicase-landing">
      <nav className="medicase-landing__nav">
        <Container className="d-flex justify-content-between align-items-center">
          <div className="medicase-landing__brand">{productName}</div>
          <div className="d-flex gap-2">
            <Button variant="outline-light" size="sm" onClick={openSignIn}>Sign in</Button>
            <Button variant="light" size="sm" onClick={openSignUp}>Get started</Button>
          </div>
        </Container>
      </nav>

      <section className="medicase-landing__hero">
        <Container>
          <Row className="justify-content-center text-center">
            <Col lg={10} xl={9}>
              <h1 className="medicase-landing__title">
                Build prescribing confidence,<br/>
                <span className="medicase-landing__title-accent">case by case.</span>
              </h1>
              <p className="medicase-landing__lede">
                {tagline || 'A simulator that mirrors the ePMA systems you\'ll meet on placement. Realistic case studies, allergies, controlled drugs, witness sign-off, audit trails. Practise safely, before the patient.'}
              </p>
              <div className="medicase-landing__cta">
                <Button size="lg" variant="primary" onClick={openSignUp}>Get started</Button>
                <Button size="lg" variant="outline-primary" onClick={openSignIn}>Sign in</Button>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="medicase-landing__features">
        <Container>
          <div className="text-center mb-5">
            <h2 className="medicase-landing__section-title">Everything in one workspace</h2>
            <p className="medicase-landing__section-sub">The same tools your faculty would build themselves, ready out of the box.</p>
          </div>
          <Row className="g-4">
            {FEATURES.map((feature) => (
              <Col key={feature.title} md={6} lg={3}>
                <div className="medicase-landing__feature">
                  <div className="medicase-landing__feature-icon">
                    <i className={`bi ${feature.icon}`} aria-hidden="true" />
                  </div>
                  <h3>{feature.title}</h3>
                  <p>{feature.body}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Pricing section hidden for university demo — restore when needed */}

      <section className="medicase-landing__university">
        <Container>
          <Row className="align-items-center g-4">
            <Col md={7}>
              <h2 className="medicase-landing__section-title">Studying through your university?</h2>
              <p className="medicase-landing__section-sub mb-0">
                Many UK pharmacy, medical and nursing schools subscribe MediCase for their cohorts (often running
                inter-professional workshops where all three disciplines collaborate on the same simulated patient).
                Your students get full access, no card needed, under their own branded subdomain. Talk to your
                faculty about whether your school subscribes, or ask us to introduce them.
              </p>
            </Col>
            <Col md={5}>
              <div className="medicase-landing__university-card">
                <h4>For institutions</h4>
                <p>Branded subdomain &middot; unlimited students &middot; assignment scheduling &middot; educator-admin dashboard</p>
                <Button variant="outline-primary" onClick={openContact}>
                  Talk to us
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <footer className="medicase-landing__footer">
        <Container className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <strong>{productName}</strong>
            <span className="text-muted ms-2">&middot; built by Auxtechna in the UK</span>
          </div>
          <div className="text-muted small">
            <a href="https://auxtechna.co.uk" target="_blank" rel="noopener noreferrer">auxtechna.co.uk</a>
          </div>
        </Container>
      </footer>

      <Modal show={showAuth} onHide={() => setShowAuth(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{authMode === 'login' ? `Sign in to ${productName}` : `Create your ${productName} account`}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Login
            onSubmit={onSubmit}
            loading={authLoading}
            error={authError}
            onForgotPassword={onForgotPassword}
            initialMode={authMode}
            onModeChange={onModeChange}
          />
        </Modal.Body>
      </Modal>

      <Modal show={showContact} onHide={closeContact} centered backdrop={contactSubmitting ? 'static' : true}>
        <Modal.Header closeButton={!contactSubmitting}>
          <Modal.Title>{productName} for your institution</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {contactStatus === 'success' ? (
            <div className="text-center py-3">
              <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '2.5rem' }} aria-hidden="true" />
              <h5 className="mt-3 mb-2">Thanks, your message is on its way.</h5>
              <p className="text-muted mb-4">
                Ben will get back to you within one working day.
              </p>
              <Button variant="primary" onClick={() => setShowContact(false)}>Close</Button>
            </div>
          ) : (
            <Form onSubmit={handleContactSubmit} noValidate>
              <p className="text-muted small mb-3">
                Tell us a little about your school or department and we&apos;ll be in touch about setting up a branded
                MediCase subdomain for your cohort.
              </p>
              <Form.Group className="mb-3" controlId="contactName">
                <Form.Label>Your name</Form.Label>
                <Form.Control
                  type="text"
                  value={contactForm.name}
                  onChange={handleContactChange('name')}
                  required
                  disabled={contactSubmitting}
                  autoComplete="name"
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="contactEmail">
                <Form.Label>Work email</Form.Label>
                <Form.Control
                  type="email"
                  value={contactForm.email}
                  onChange={handleContactChange('email')}
                  required
                  disabled={contactSubmitting}
                  autoComplete="email"
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="contactInstitution">
                <Form.Label>Institution</Form.Label>
                <Form.Control
                  type="text"
                  value={contactForm.institution}
                  onChange={handleContactChange('institution')}
                  placeholder="e.g. University of Manchester, School of Pharmacy"
                  required
                  disabled={contactSubmitting}
                  autoComplete="organization"
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="contactMessage">
                <Form.Label>What would you like to discuss? <span className="text-muted small">(optional)</span></Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={contactForm.message}
                  onChange={handleContactChange('message')}
                  disabled={contactSubmitting}
                  placeholder="Cohort size, timelines, any specific requirements..."
                />
              </Form.Group>
              {contactStatus === 'validation' && (
                <div className="alert alert-warning small py-2 mb-3">
                  Please fill in your name, email and institution.
                </div>
              )}
              {contactStatus === 'error' && (
                <div className="alert alert-danger small py-2 mb-3">
                  Something went wrong sending your message. Please email <a href="mailto:ben.logan@auxtechna.co.uk">ben.logan@auxtechna.co.uk</a> directly.
                </div>
              )}
              <div className="d-flex justify-content-end gap-2">
                <Button variant="outline-secondary" onClick={closeContact} disabled={contactSubmitting}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" disabled={contactSubmitting}>
                  {contactSubmitting ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" aria-hidden="true" />
                      Sending...
                    </>
                  ) : 'Send'}
                </Button>
              </div>
            </Form>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default StudentMarketingLanding;
